import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import api from '@/services/api';
import OfficialSourceMultiSelect from '@/components/review/OfficialSourceMultiSelect';
import type { OfficialSourceOption } from '@/components/review/officialSource';
import { canSetRemarkDeadline, sharePermissionLabel } from '@/components/review/sharePermission';
import { useAuth } from '@/hooks/useAuth';
import type { SessionShare } from '@/types';

export type { OfficialSourceOption };

interface Props {
  open: boolean;
  share: SessionShare | null;
  targetLabel: string;
  /** Show publish toggle for group shares with 可備註 permission */
  canReleaseOfficial?: boolean;
  sessionOwnerUserId?: number;
  officialSourceOptions?: OfficialSourceOption[];
  onClose: () => void;
  onSaved: () => void;
}

function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ShareEditDialog({
  open,
  share,
  targetLabel,
  canReleaseOfficial = false,
  sessionOwnerUserId,
  officialSourceOptions = [],
  onClose,
  onSaved,
}: Props) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [permission, setPermission] = useState<'participant' | 'viewer'>('participant');
  const [dueAt, setDueAt] = useState('');
  const [message, setMessage] = useState('');
  const [officialPublished, setOfficialPublished] = useState(false);
  const [officialSourceUserIds, setOfficialSourceUserIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ownerId = sessionOwnerUserId ?? 0;

  const sourceOptions = useMemo(() => {
    const map = new Map<number, OfficialSourceOption>();
    if (ownerId) {
      const ownerLabel =
        currentUserId != null && Number(ownerId) === Number(currentUserId)
          ? '您'
          : officialSourceOptions.find((o) => o.user_id === ownerId)?.label || '影片擁有者';
      map.set(ownerId, { user_id: ownerId, label: ownerLabel });
    }
    for (const opt of officialSourceOptions) {
      if (!map.has(opt.user_id)) map.set(opt.user_id, opt);
    }
    return [...map.values()];
  }, [officialSourceOptions, ownerId, currentUserId]);

  useEffect(() => {
    if (!open || !share) return;
    setPermission(share.permission);
    setDueAt(toDatetimeLocalValue(share.due_at));
    setMessage(share.message || '');
    setOfficialPublished(!!share.official_released_at);
    const initialIds =
      share.official_source_user_ids?.length
        ? share.official_source_user_ids
        : share.official_source_user_id != null
          ? [share.official_source_user_id]
          : ownerId
            ? [ownerId]
            : [];
    setOfficialSourceUserIds(initialIds);
    setError(null);
  }, [open, share, ownerId]);

  const showDeadline = canSetRemarkDeadline(permission);
  const showPublishOption =
    canReleaseOfficial && share?.share_type === 'group' && permission === 'participant';

  const handleSave = async () => {
    if (!share) return;
    setSaving(true);
    setError(null);
    try {
      const patchBody: Record<string, unknown> = {
        permission,
        message: message || null,
        due_at: showDeadline && dueAt ? dueAt : null,
      };
      if (showPublishOption && ownerId) {
        patchBody.official_source_user_ids = officialSourceUserIds.length
          ? officialSourceUserIds
          : [ownerId];
      }

      await api.patch(`/shares/${share.id}`, patchBody);

      if (share.share_type === 'group' && canReleaseOfficial) {
        const wasPublished = !!share.official_released_at;
        const wantPublished = permission === 'participant' ? officialPublished : false;
        if (wasPublished !== wantPublished) {
          await api.post(`/review-sessions/${share.session_id}/release-official`, {
            share_id: share.id,
            group_id: share.target_group_id ?? undefined,
            published: wantPublished,
          });
        }
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '更新失敗';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>編輯分享設定</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!share && <Alert severity="warning">找不到此群組的分享設定</Alert>}
          <Typography variant="body2" color="text.secondary">
            {targetLabel}
          </Typography>
          {share && (
            <>
              <TextField
                select
                label="分享權限"
                value={permission}
                onChange={(e) => {
                  const next = e.target.value as 'participant' | 'viewer';
                  setPermission(next);
                  if (!canSetRemarkDeadline(next)) setDueAt('');
                }}
                fullWidth
              >
                <MenuItem value="participant">{sharePermissionLabel('participant')}</MenuItem>
                <MenuItem value="viewer">{sharePermissionLabel('viewer')}</MenuItem>
              </TextField>
              {showDeadline && (
                <TextField
                  label="備註截止時間"
                  type="datetime-local"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              )}
              <TextField
                label="附言"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
              {showPublishOption && (
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Stack spacing={1.5}>
                    {sourceOptions.length > 0 && (
                      <OfficialSourceMultiSelect
                        options={sourceOptions}
                        value={officialSourceUserIds}
                        onChange={setOfficialSourceUserIds}
                        ownerId={ownerId}
                        currentUserId={currentUserId}
                      />
                    )}
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          發佈官方判決
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          發佈後，群組參與者可對照官方判決
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          color={officialPublished ? 'success' : 'error'}
                          variant="outlined"
                          label={officialPublished ? '已發佈' : '未發佈'}
                          sx={{ height: 22 }}
                        />
                        <Switch
                          size="small"
                          color="secondary"
                          checked={officialPublished}
                          onChange={(_, checked) => setOfficialPublished(checked)}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" color="secondary" disabled={saving || !share} onClick={handleSave}>
          儲存
        </Button>
      </DialogActions>
    </Dialog>
  );
}
